import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { User as UserIcon, Pencil, X, RefreshCw, ArrowLeft, Bell, Radio, HelpCircle, UserPlus, Mail, LogOut } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import PostComposer from "@/components/social/PostComposer";
import PostCard from "@/components/social/PostCard";
import ReelsViewer from "@/components/social/ReelsViewer";
import FriendsPage from "@/components/social/FriendsPage";
import SocialInbox from "@/components/social/SocialInbox";
import SocialThread from "@/components/social/SocialThread";
import SocialProfile from "@/components/social/SocialProfile";
import StoriesBar from "@/components/social/StoriesBar";
import HelpGuide from "@/components/shared/HelpGuide";
import SocialConsentDialog from "@/components/social/SocialConsentDialog";

export default function SocialPage({ socialMsg, newPostSignal, openPostId, onPostOpened, onImmersiveChange, onLeaveSocial }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [leaveOpen, setLeaveOpen] = useState(false); // "Leave Social" confirmation
  const [leaveBusy, setLeaveBusy] = useState(false);
  const [view, setView] = useState("feed"); // feed | friends | inbox | me
  const [guide, setGuide] = useState(false);
  const [feedScope, setFeedScope] = useState("discover"); // discover | following
  const [category, setCategory] = useState(null); // active topic-filter chip (null = all)
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileId, setProfileId] = useState(null); // open someone's profile
  const [threadPeer, setThreadPeer] = useState(null); // open a social DM thread
  const [inboxKey, setInboxKey] = useState(0);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [single, setSingle] = useState(null); // deep-linked single post {loading|post|error}
  const [discoverNew, setDiscoverNew] = useState(0); // new-posts badge on Discover
  const [composerOpen, setComposerOpen] = useState(false); // floating "Create post" sheet
  const [composeFrom, setComposeFrom] = useState("feed"); // where the composer was opened from: "feed" | "profile"
  const [profileRefresh, setProfileRefresh] = useState(0);
  const [reels, setReels] = useState(null); // {items, idx} — Shorts-style fullscreen video pager
  const [menuOpen, setMenuOpen] = useState(false); // X-style left slide-out menu
  const [hideBars, setHideBars] = useState(false); // X-style: slide top bars away on scroll-down
  const [pull, setPull] = useState(0); // pull-to-refresh drag distance (px)
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const feedSeed = useRef(null);      // current Discover ranking seed (stable across paging)
  const feedOffset = useRef(0);       // how deep we've paged into the ranked pool
  const feedMore = useRef(false);     // more ranked items available below (progressive disclosure)
  const eventsQueue = useRef([]);     // batched engagement events
  const dwellSince = useRef({});      // pid -> timestamp while visible
  const viewedSet = useRef(new Set()); // pids already counted as a 'view'
  const maxScroll = useRef(0);
  const feedObserver = useRef(null);
  const lastScrollY = useRef(0);
  const immersiveRef = useRef(false);
  const scrollRef = useRef(null);
  const feedScrollTop = useRef(0);   // remembered feed scroll position
  const shouldRestore = useRef(false); // restore feed scroll after closing a post window
  const touchStartY = useRef(null);
  const edgeStartX = useRef(null); // left-edge swipe to open the menu
  const createLabel = (localStorage.getItem("rc_lang") === "fr") ? "Créer une publication" : "Create post";

  // Toggle immersive (hide top bars + dim bottom nav) and notify parent — debounced by ref.
  const setImmersive = useCallback((v) => {
    if (immersiveRef.current === v) return;
    immersiveRef.current = v;
    setHideBars(v);
    onImmersiveChange && onImmersiveChange(v);
  }, [onImmersiveChange]);

  const onScroll = (e) => {
    const el = e.target;
    const y = el.scrollTop;
    // scroll-depth metric + progressive-disclosure paging
    const denom = el.scrollHeight - el.clientHeight;
    if (denom > 0) maxScroll.current = Math.max(maxScroll.current, y / denom);
    if (view === "feed" && feedScope === "discover" && y + el.clientHeight >= el.scrollHeight - 700) loadMore();
    const prev = lastScrollY.current;
    if (y < 12) { setImmersive(false); lastScrollY.current = y; return; }
    const delta = y - prev;
    if (Math.abs(delta) < 8) return;
    setImmersive(delta > 0); // scrolling down the feed -> hide; scrolling up -> show
    lastScrollY.current = y;
  };

  // Pull-to-refresh (feed view only) — drag down from the very top to reload.
  const PULL_MAX = 90, PULL_TRIGGER = 60;
  const onTouchStart = (e) => {
    edgeStartX.current = (e.touches[0].clientX <= 28 && !menuOpen) ? e.touches[0].clientX : null;
    if (view !== "feed" || refreshing || !scrollRef.current || scrollRef.current.scrollTop > 0) { touchStartY.current = null; return; }
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchMove = (e) => {
    // Left-edge horizontal swipe opens the side menu (X-style).
    if (edgeStartX.current != null && !menuOpen) {
      const dx = e.touches[0].clientX - edgeStartX.current;
      if (dx > 60) { setMenuOpen(true); edgeStartX.current = null; return; }
    }
    if (touchStartY.current == null || refreshing) return;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (dy > 0 && scrollRef.current && scrollRef.current.scrollTop <= 0) {
      setPull(Math.min(dy * 0.5, PULL_MAX));
    } else {
      setPull(0);
    }
  };
  const onTouchEnd = async () => {
    edgeStartX.current = null;
    if (refreshing) return;
    if (pull >= PULL_TRIGGER) {
      setRefreshing(true); setPull(PULL_TRIGGER);
      try { await loadFeed(); } finally { setRefreshing(false); setPull(0); }
    } else {
      setPull(0);
    }
    touchStartY.current = null;
  };

  // Any view/overlay change should reveal the bars again.
  useEffect(() => { setImmersive(false); }, [view, profileId, threadPeer, single, setImmersive]);

  // Restore the feed scroll position after returning from a post window.
  // Re-apply for a few frames so async content (stories/images) can't clamp it to 0.
  useLayoutEffect(() => {
    if (single || !shouldRestore.current) return;
    const target = feedScrollTop.current;
    const apply = () => { if (scrollRef.current) scrollRef.current.scrollTop = target; };
    apply();
    const raf = requestAnimationFrame(apply);
    let n = 0;
    const id = setInterval(() => { apply(); if (++n >= 8) { clearInterval(id); shouldRestore.current = false; } }, 60);
    return () => { cancelAnimationFrame(raf); clearInterval(id); };
  }, [single]);

  useEffect(() => {
    if (!openPostId) return;
    setSingle({ loading: true });
    api.get(`/social/posts/${openPostId}`).then(({ data }) => setSingle({ post: data })).catch(() => setSingle({ error: true }));
    onPostOpened && onPostOpened();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPostId]);

  useEffect(() => {
    api.get("/social/inbox").then(({ data }) => setInboxUnread(data.reduce((s, c) => s + (c.unread || 0), 0))).catch(() => {});
  }, [inboxKey, socialMsg]);

  // Discover is the default open feed, so treat it as seen on mount (no stale badge on the active tab).
  useEffect(() => {
    localStorage.setItem("rc_discover_seen", new Date().toISOString());
    setDiscoverNew(0);
  }, []);

  // New-posts badge on Discover: count public posts from others since the user last opened Discover.
  useEffect(() => {
    const seen = localStorage.getItem("rc_discover_seen") || new Date().toISOString();
    api.get("/social/discover/new-count", { params: { since: seen } })
      .then(({ data }) => setDiscoverNew(data.count || 0)).catch(() => {});
  }, [socialMsg]);

  const openDiscover = () => {
    localStorage.setItem("rc_discover_seen", new Date().toISOString());
    setDiscoverNew(0);
    setCategory(null);
    setFeedScope("discover"); loadFeed("discover");
  };

  // (loadCategory defined below after loadFeed to avoid TDZ)

  // Live: tick the Discover badge up as others post (unless already viewing Discover).
  useEffect(() => {
    if (!newPostSignal) return;
    if (newPostSignal.author_id === (user && user.id)) return;
    if (view === "feed" && feedScope === "discover") {
      localStorage.setItem("rc_discover_seen", new Date().toISOString());
      return;
    }
    setDiscoverNew((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPostSignal]);

  // Engagement events: batched to /social/events (feeds the Discover re-ranking).
  const queueEvent = useCallback((ev) => { eventsQueue.current.push(ev); }, []);
  const flushEvents = useCallback(async () => {
    if (!eventsQueue.current.length) return;
    const batch = eventsQueue.current.splice(0, 50);
    try { await api.post("/social/events", { events: batch }); } catch { eventsQueue.current.unshift(...batch); }
  }, []);
  useEffect(() => {
    const iv = setInterval(flushEvents, 5000);
    const onVis = () => { if (document.visibilityState === "hidden") flushEvents(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); flushEvents(); };
  }, [flushEvents]);

  const loadFeed = useCallback(async (scope) => {
    setLoading(true);
    const s = scope || feedScope;
    try {
      if (s === "following") {
        const { data } = await api.get("/social/feed", { params: { limit: 20, scope: s } });
        setFeed(data);
      } else {
        // Discover: fresh seed → a new order every load/refresh (never identical twice).
        viewedSet.current = new Set(); dwellSince.current = {}; maxScroll.current = 0;
        const res = await api.get("/social/feed", { params: { limit: 12, scope: "discover" } });
        feedSeed.current = res.headers["x-feed-seed"] || null;
        feedMore.current = res.headers["x-feed-more"] === "1";
        feedOffset.current = res.data.length;
        setFeed(res.data);
      }
    } catch {} finally { setLoading(false); }
  }, [feedScope]);

  // Topic-filter chips: tap a category to show only posts tagged with it (chronological).
  const loadCategory = useCallback(async (cat) => {
    const next = category === cat ? null : cat; // tapping the active chip clears it
    setCategory(next);
    feedMore.current = false;
    if (!next) { setFeedScope("discover"); loadFeed("discover"); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/social/feed", { params: { topic: next, limit: 30 } });
      setFeed(data);
    } catch {} finally { setLoading(false); }
  }, [category, loadFeed]);

  // Progressive disclosure: page deeper into the SAME ranked pool (same seed) as the user scrolls.
  const loadMore = useCallback(async () => {
    if (feedScope !== "discover" || category || !feedMore.current || loadingMore || feedSeed.current == null) return;
    setLoadingMore(true);
    try {
      const res = await api.get("/social/feed", {
        params: { scope: "discover", seed: feedSeed.current, offset: feedOffset.current, limit: 12 },
      });
      feedMore.current = res.headers["x-feed-more"] === "1";
      feedOffset.current += res.data.length;
      setFeed((f) => {
        const seen = new Set(f.map((x) => x.id));
        return [...f, ...res.data.filter((x) => !seen.has(x.id))];
      });
    } catch {} finally { setLoadingMore(false); }
  }, [feedScope, loadingMore]);
  useEffect(() => { loadFeed(); }, [loadFeed]);

  // Dwell + view tracking via IntersectionObserver (drives affinity + engagement metrics).
  useEffect(() => {
    if (view !== "feed" || !scrollRef.current) return;
    const root = scrollRef.current;
    const obs = new IntersectionObserver((entries) => {
      const now = Date.now();
      entries.forEach((e) => {
        const pid = e.target.dataset.pid;
        const idx = parseInt(e.target.dataset.idx || "0", 10);
        if (!pid) return;
        if (e.isIntersecting && e.intersectionRatio >= 0.5) {
          dwellSince.current[pid] = now;
          if (!viewedSet.current.has(pid)) { viewedSet.current.add(pid); queueEvent({ type: "view", post_id: pid }); }
        } else if (dwellSince.current[pid]) {
          const dwell = now - dwellSince.current[pid];
          delete dwellSince.current[pid];
          const depth = feed.length ? (idx + 1) / feed.length : 0;
          if (dwell >= 2000) queueEvent({ type: "dwell", post_id: pid, dwell_ms: dwell, scroll_depth: depth });
        }
      });
    }, { root, threshold: [0, 0.5] });
    root.querySelectorAll("[data-feed-card]").forEach((el) => obs.observe(el));
    feedObserver.current = obs;
    return () => obs.disconnect();
  }, [feed, view, queueEvent]);

  const onEngage = useCallback((type, postId) => queueEvent({ type, post_id: postId }), [queueEvent]);

  // "Not interested" with an Undo snackbar — remove immediately, restore on Undo (mistap safety).
  const handleNotInterested = useCallback((post) => {
    let idx = -1;
    setFeed((f) => { idx = f.findIndex((x) => x.id === post.id); return f.filter((x) => x.id !== post.id); });
    api.post("/social/hide", { post_id: post.id }).catch(() => {});
    toast(t("s_not_interested_toast"), {
      action: {
        label: t("s_undo"),
        onClick: () => {
          api.post("/social/unhide", { post_id: post.id }).catch(() => {});
          setFeed((f) => { if (f.some((x) => x.id === post.id)) return f; const c = [...f]; c.splice(idx < 0 ? 0 : Math.min(idx, c.length), 0, post); return c; });
        },
      },
    });
  }, [t]);

  const openProfile = (id) => { setThreadPeer(null); setProfileId(id); };
  const openThread = (peer) => { setProfileId(null); setThreadPeer(peer); };

  // Flatten every video in the current feed into a single Shorts-style reel list.
  const feedReels = React.useMemo(() => {
    const list = [];
    feed.forEach((p) => {
      const base = p.reshared || p;
      (base.media || []).forEach((m) => {
        if (m.type === "video") list.push({ post: p, url: m.url, poster: m.poster });
      });
    });
    return list;
  }, [feed]);
  const openReel = (postId, url) => {
    queueEvent({ type: "click", post_id: postId });
    const start = feedReels.find((r) => r.post.id === postId && r.url === url);
    const rest = feedReels.filter((r) => !(r.post.id === postId && r.url === url));
    // Diverse order (X/Shorts-style): keep the tapped video first, shuffle the rest.
    for (let i = rest.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [rest[i], rest[j]] = [rest[j], rest[i]]; }
    setReels({ items: start ? [start, ...rest] : rest, idx: 0 });
  };

  // Full-screen overlays first
  if (single) {
    return (
      <div className="h-full flex flex-col bg-[var(--rc-bg)] rc-safe-top" data-testid="social-page">
        <div className="shrink-0 px-4 pt-4 pb-2">
          <button onClick={() => setSingle(null)} data-testid="single-post-back" className="text-sm text-[var(--rc-gold)]">← {t("back") || "Back"}</button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-2">
          <div className="max-w-xl mx-auto">
            {single.loading ? <div className="text-center text-[var(--rc-muted)] py-16">…</div>
              : single.error ? <div className="text-center text-[var(--rc-muted)] py-16" data-testid="single-post-error">{t("share_not_found")}</div>
              : <PostCard post={single.post} forceComments stickyComments onOpenProfile={openProfile} onOpenReel={openReel} onEngage={onEngage} onDeleted={() => setSingle(null)} onHideAuthor={() => setSingle(null)} />}
          </div>
        </div>
      </div>
    );
  }
  if (threadPeer) {
    return (
      <div className="h-full bg-[var(--rc-bg)] px-4 pt-4 rc-safe-top" data-testid="social-page">
        <SocialThread peer={threadPeer} socketMessage={socialMsg} onBack={() => { setThreadPeer(null); setInboxKey((k) => k + 1); }} />
      </div>
    );
  }
  if (profileId) {
    return (
      <div className="h-full overflow-y-auto no-scrollbar bg-[var(--rc-bg)] px-4 pt-4 pb-24 rc-safe-top" data-testid="social-page">
        <SocialProfile userId={profileId} onBack={() => setProfileId(null)} onOpenProfile={openProfile} onOpenThread={openThread} />
      </div>
    );
  }

  const sectionTitle = view === "friends" ? t("s_friends") : view === "inbox" ? t("s_inbox") : t("s_me");
  const menuItems = [
    { key: "me", icon: UserIcon, label: t("s_me"), onClick: () => { setView("me"); setMenuOpen(false); } },
    { key: "friends", icon: UserPlus, label: t("s_friends"), onClick: () => { setView("friends"); setMenuOpen(false); } },
    { key: "inbox", icon: Bell, label: t("s_inbox"), badge: inboxUnread, onClick: () => { setView("inbox"); setMenuOpen(false); } },
    { key: "live", icon: Radio, label: t("s_live") || "Live", soon: true, onClick: () => { setMenuOpen(false); toast.message(t("s_live_soon")); } },
    { key: "help", icon: HelpCircle, label: t("guide") || "Help", onClick: () => { setMenuOpen(false); setGuide(true); } },
  ];

  return (
    <div ref={scrollRef} className="relative h-full overflow-y-auto no-scrollbar bg-[var(--rc-bg)]" data-testid="social-page"
      onScroll={onScroll} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      {(pull > 0 || refreshing) && (
        <div className="absolute left-0 right-0 top-0 z-30 flex justify-center pointer-events-none" style={{ transform: `translateY(${Math.max(pull - 20, 4)}px)` }} data-testid="ptr-indicator">
          <div className="h-9 w-9 grid place-items-center rounded-full bg-[var(--rc-surface)] border border-[var(--rc-border)] shadow-lg">
            <RefreshCw size={18} className={`text-[var(--rc-gold)] ${refreshing ? "animate-spin" : ""}`} style={refreshing ? undefined : { transform: `rotate(${pull * 3}deg)` }} />
          </div>
        </div>
      )}
      <div className="rc-safe-top px-4 pt-2 pb-24 transition-transform mx-auto w-full max-w-[520px] sm:min-h-full sm:border-x sm:border-[var(--rc-border)]" style={{ transform: pull ? `translateY(${pull}px)` : undefined }}>
        {view === "feed" ? (
          <>
            {/* Tight header: avatar (opens menu) · Discover/Following tabs */}
            <div data-testid="social-header"
              className={`sticky top-0 z-20 -mx-4 px-4 py-2 bg-[var(--rc-bg)] flex items-center gap-2 transition-[transform,opacity] duration-300 ease-out ${hideBars ? "-translate-y-[130%] opacity-0 pointer-events-none" : "translate-y-0 opacity-100"}`}>
              <button onClick={() => setMenuOpen(true)} data-testid="social-menu-btn" aria-label="Menu"
                className="shrink-0 h-9 w-9 rounded-xl overflow-hidden border border-[var(--rc-border)] bg-[var(--rc-surface)] grid place-items-center text-[var(--rc-gold)] font-bold">
                {user?.avatar ? <img src={user.avatar} alt="" className="h-full w-full object-cover" /> : (user?.name || "?").slice(0, 1).toUpperCase()}
              </button>
              <div className="flex-1 flex items-center justify-center gap-8">
                {[["discover", t("s_discover")], ["following", t("s_following")]].map(([k, l]) => (
                  <button key={k} onClick={() => { if (k === "discover") openDiscover(); else { setCategory(null); setFeedScope(k); loadFeed(k); } }} data-testid={`feed-scope-${k}`}
                    className={`relative pb-1.5 text-base font-semibold transition-colors ${feedScope === k ? "text-[var(--rc-text)]" : "text-[var(--rc-muted)]"}`}>
                    <span className="inline-flex items-center gap-1.5">{l}
                      {k === "discover" && discoverNew > 0 && <span data-testid="discover-new-badge" className="h-4 min-w-4 px-1 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black text-[10px] font-bold leading-none">{discoverNew > 9 ? "9+" : discoverNew}</span>}
                    </span>
                    {feedScope === k && <span className="absolute -bottom-px left-1/2 -translate-x-1/2 h-0.5 w-8 rounded-full bg-[var(--rc-gold)]" />}
                  </button>
                ))}
              </div>
              <button onClick={() => setView("inbox")} data-testid="social-header-inbox" aria-label={t("s_inbox")}
                className="relative shrink-0 h-9 w-9 grid place-items-center rounded-full text-[var(--rc-text)] hover:bg-[var(--rc-surface)]">
                <Mail size={22} />
                {inboxUnread > 0 && <span className="absolute top-0.5 right-0.5 h-4 min-w-4 px-1 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black text-[10px] font-bold leading-none">{inboxUnread > 9 ? "9+" : inboxUnread}</span>}
              </button>
            </div>

            <div className="mt-2"><StoriesBar refreshSignal={socialMsg} /></div>

            <div className="-mx-4 mt-3 border-t border-[var(--rc-border)]">
              {loading ? <div className="text-center py-10 text-[var(--rc-muted)]">{t("s_loading")}</div>
                : feed.length ? (<>
                    {feed.map((p, i) => (
                      <div key={p.id} data-feed-card data-pid={p.id} data-idx={i}>
                        <PostCard post={p} onOpenProfile={openProfile} onOpenReel={openReel} onEngage={onEngage} onNotInterested={handleNotInterested}
                          onOpenDetail={(pp) => { feedScrollTop.current = scrollRef.current?.scrollTop || 0; shouldRestore.current = true; queueEvent({ type: "click", post_id: pp.id }); setSingle({ post: pp }); }}
                          onDeleted={(id) => setFeed((f) => f.filter((x) => x.id !== id))}
                          onHideAuthor={(aid) => setFeed((f) => f.filter((x) => x.author_id !== aid && x.reshared?.author_id !== aid))} />
                      </div>
                    ))}
                    {feedScope === "discover" && loadingMore && (
                      <div className="text-center py-6 text-[var(--rc-muted)]" data-testid="feed-loading-more"><RefreshCw size={18} className="inline animate-spin text-[var(--rc-gold)]" /></div>
                    )}
                  </>)
                  : feedScope === "following" ? (
                    <div className="text-center py-16 px-6" data-testid="following-empty">
                      <p className="text-[var(--rc-text)] font-medium">{t("s_following_empty")}</p>
                      <p className="text-sm text-[var(--rc-muted)] mt-1">{t("s_following_empty_sub")}</p>
                      <button onClick={() => setView("friends")} data-testid="find-people-btn"
                        className="mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold px-5 py-2.5 active:scale-95 transition-transform">
                        <UserPlus size={16} /> {t("s_find_people")}
                      </button>
                    </div>
                  ) : <div className="text-center py-16 text-[var(--rc-muted)]">{t("s_no_posts")}</div>}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3 pt-1">
              <button onClick={() => setView("feed")} data-testid="social-back-btn" className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-text)] hover:bg-[var(--rc-surface)]"><ArrowLeft size={20} /></button>
              <h2 className="font-heading text-2xl">{sectionTitle}</h2>
            </div>
            {view === "friends" && <FriendsPage onOpenProfile={openProfile} onOpenThread={openThread} />}
            {view === "inbox" && <SocialInbox onOpenThread={openThread} refreshKey={inboxKey + (socialMsg ? 1 : 0)} />}
            {view === "me" && <SocialProfile userId={user.id} onOpenProfile={openProfile} onOpenThread={openThread} onCompose={() => { setComposeFrom("profile"); setComposerOpen(true); }} refreshKey={profileRefresh} />}
          </>
        )}
      </div>

      {guide && (
        <HelpGuide testid="social-guide-overlay" title={t("guide_social_title")}
          steps={[t("guide_social_1"), t("guide_social_2"), t("guide_social_3"), t("guide_social_4")]}
          onClose={() => setGuide(false)} />
      )}

      {/* Floating "Create post" button — feed only */}
      {view === "feed" && !composerOpen && (
        <button onClick={() => { setComposeFrom("feed"); setComposerOpen(true); }} data-testid="social-fab-compose" aria-label={createLabel}
          className="fixed bottom-24 right-5 sm:right-auto sm:left-[calc(50%+200px)] z-40 h-14 w-14 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black shadow-[0_10px_30px_-6px_var(--rc-gold)] active:scale-95 transition-transform">
          <Pencil size={22} strokeWidth={2.5} />
        </button>
      )}

      {/* Left slide-out menu (X-style) */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div className="fixed inset-0 z-50" data-testid="social-side-menu"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} data-testid="social-menu-backdrop" />
            <motion.aside className="absolute left-0 top-0 h-full w-[82%] max-w-xs bg-[var(--rc-bg)] border-r border-[var(--rc-border)] overflow-y-auto no-scrollbar rc-safe-top touch-pan-y"
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "spring", damping: 32, stiffness: 320 }}
              drag="x" dragDirectionLock dragConstraints={{ left: 0, right: 0 }} dragElastic={{ left: 0.9, right: 0 }} dragMomentum={false}
              onDragEnd={(e, info) => { if (info.offset.x < -80 || info.velocity.x < -400) setMenuOpen(false); }}>
              <button onClick={() => { setView("me"); setMenuOpen(false); }} data-testid="social-menu-profile"
                className="w-full flex items-center gap-3 p-5 border-b border-[var(--rc-border)] text-left">
                <span className="h-14 w-14 rounded-2xl overflow-hidden border border-[var(--rc-border)] bg-[var(--rc-surface)] grid place-items-center text-[var(--rc-gold)] text-xl font-bold shrink-0">
                  {user?.avatar ? <img src={user.avatar} alt="" className="h-full w-full object-cover" /> : (user?.name || "?").slice(0, 1).toUpperCase()}
                </span>
                <span className="min-w-0">
                  <span className="block font-heading text-lg truncate">{user?.name} {user?.flag || ""}</span>
                  <span className="block text-sm text-[var(--rc-muted)] truncate">{user?.email}</span>
                </span>
              </button>
              <nav className="py-2">
                {menuItems.map((m) => (
                  <button key={m.key} onClick={m.onClick} data-testid={`social-menu-${m.key}`}
                    className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-[var(--rc-surface)] transition-colors">
                    <m.icon size={22} className="text-[var(--rc-gold)] shrink-0" />
                    <span className="text-lg font-medium">{m.label}</span>
                    {m.soon && <span className="ml-1 rounded-full bg-[var(--rc-gold)]/20 text-[var(--rc-gold)] text-[10px] font-bold px-2 py-0.5 tracking-wide">SOON</span>}
                    {m.badge > 0 && <span className="ml-auto h-5 min-w-5 px-1.5 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black text-xs font-bold">{m.badge > 9 ? "9+" : m.badge}</span>}
                  </button>
                ))}
                <div className="my-2 border-t border-[var(--rc-border)]" />
                <button onClick={() => { setMenuOpen(false); setLeaveOpen(true); }} data-testid="social-menu-leave"
                  className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-red-500/10 transition-colors text-red-400">
                  <LogOut size={22} className="shrink-0" />
                  <span className="text-lg font-medium">{t("s_leave")}</span>
                </button>
              </nav>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {composerOpen && (
          <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setComposerOpen(false)} data-testid="social-compose-sheet">
            <motion.div className="w-full max-w-xl bg-[var(--rc-bg)] rounded-t-3xl p-4 pb-8 rc-safe-bottom max-h-[90vh] overflow-y-auto no-scrollbar"
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}>
              <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[var(--rc-border)]" />
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-2xl">{createLabel}</h3>
                <button onClick={() => setComposerOpen(false)} data-testid="social-compose-close" className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-muted)] hover:bg-[var(--rc-surface)]"><X size={20} /></button>
              </div>
              <PostComposer onPosted={(p) => {
                // Posting from the FEED pins the new post to the top, marked "now",
                // until the next refresh (loadFeed) restores normal ranking.
                // Posting from a profile keeps the previous behaviour.
                const post = composeFrom === "feed" ? { ...p, __pinnedNow: true } : p;
                setFeed((f) => [post, ...f.filter((x) => x.id !== post.id)]);
                setComposerOpen(false);
                setProfileRefresh((k) => k + 1);
              }} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {reels && <ReelsViewer reels={reels.items} startIndex={reels.idx} onClose={() => setReels(null)} onOpenProfile={openProfile} />}

      <SocialConsentDialog open={leaveOpen} variant="leave" busy={leaveBusy}
        onCancel={() => setLeaveOpen(false)}
        onConfirm={async () => {
          setLeaveBusy(true);
          try { await onLeaveSocial?.(); setLeaveOpen(false); }
          finally { setLeaveBusy(false); }
        }} />
    </div>
  );
}
